import { useMemo, useRef, useState } from "react";
import { Camera, Ruler, Square, Route, Home, Cable, Flame, X, Loader2, Plus, Check, RefreshCw, Lightbulb, Trash2, Info, LayoutGrid } from "lucide-react";
import { toast } from "sonner";
import { studio, apiError, inputCls, btnPrimary, btnGhost } from "@/lib/studioApi";

const GOALS = [
  { id: "distance", label: "Distance", icon: Ruler, hint: "Longueur, largeur, hauteur" },
  { id: "surface", label: "Surface", icon: Square, hint: "Mur, sol, plafond en m²" },
  { id: "tuyauterie", label: "Tuyauterie", icon: Cable, hint: "Longueur de tubes à poser" },
  { id: "toiture", label: "Toiture", icon: Home, hint: "Pans, faîte, surface" },
  { id: "piece", label: "Pièce", icon: LayoutGrid, hint: "Dimensions et surface au sol" },
  { id: "tubage", label: "Tubage cheminée", icon: Flame, hint: "Parcours, coudes, hauteur" },
];
const CONF = { haute: "bg-emerald-50 text-emerald-700 border-emerald-200", moyenne: "bg-amber-50 text-amber-800 border-amber-200", basse: "bg-rose-50 text-rose-700 border-rose-200" };
const COLORS = ["#3A5CC8", "#F97316", "#059669", "#7C3AED", "#DC2626", "#0891B2"];

const pxLen = (a, b, w, h) => Math.hypot((b[0] - a[0]) * w, (b[1] - a[1]) * h);
const pathLen = (pts, w, h) => pts.slice(1).reduce((s, p, i) => s + pxLen(pts[i], p, w, h), 0);
const polyArea = (pts, w, h) => Math.abs(pts.reduce((s, p, i) => { const q = pts[(i + 1) % pts.length]; return s + (p[0] * w) * (q[1] * h) - (q[0] * w) * (p[1] * h); }, 0) / 2);
const fmt = (v, unit) => `${Number(v || 0).toLocaleString("fr-BE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${unit}`;

/* Caméra intelligente : photo → l'IA repère une référence, propose les mesures ; l'artisan ajuste les points, recalibre, ajoute au devis. */
export const MeasureCamera = ({ open, onClose, onAdd, vatPercent = 21 }) => {
  const [goal, setGoal] = useState("distance");
  const [hint, setHint] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [measures, setMeasures] = useState([]);
  const [scale, setScale] = useState(null); // mètres par pixel (image analysée)
  const [drag, setDrag] = useState(null);
  const [adding, setAdding] = useState(null); // { type, points }
  const [calib, setCalib] = useState({ id: "", value: "" });
  const fileRef = useRef(null);
  const svgRef = useRef(null);

  const reset = () => { setResult(null); setMeasures([]); setScale(null); setAdding(null); setCalib({ id: "", value: "" }); setCalibrated(false); };

  const analyze = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("goal", goal);
      fd.append("hint", hint);
      const { data } = await studio.post("/measure/analyze", fd, { timeout: 180000 });
      const { width: w, height: h } = data;
      let s = null;
      if (data.reference) s = (data.reference.real_cm / 100) / Math.max(1, pxLen(data.reference.from, data.reference.to, w, h));
      else if (data.measurements[0]) {
        const m = data.measurements[0];
        const px = m.type === "area" ? Math.sqrt(polyArea(m.points, w, h)) : pathLen(m.points, w, h);
        s = m.value > 0 && px > 0 ? (m.type === "area" ? Math.sqrt(m.value) / px : m.value / px) : null;
      }
      setResult(data);
      setScale(s);
      setMeasures(data.measurements.map((m, i) => ({ ...m, color: COLORS[i % COLORS.length], ai_value: m.value })));
      if (!data.measurements.length) toast.message("Aucune mesure proposée : ajoutez vos points à la main ou reprenez la photo avec une référence (feuille A4, mètre).");
    } catch (e) {
      toast.error(apiError(e, "Analyse impossible."));
    } finally {
      setBusy(false);
    }
  };

  const valueOf = (m) => {
    if (!result) return 0;
    const { width: w, height: h } = result;
    if (!scale) return m.ai_value || 0;
    if (m.type === "area") return polyArea(m.points, w, h) * scale * scale;
    return pathLen(m.points, w, h) * scale;
  };

  const toNorm = (e) => {
    const r = svgRef.current.getBoundingClientRect();
    return [Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), Math.min(1, Math.max(0, (e.clientY - r.top) / r.height))];
  };

  const onSvgPointer = (e) => {
    if (adding) {
      const pts = [...adding.points, toNorm(e)];
      if (adding.type === "distance" && pts.length === 2) return finishAdd(pts);
      return setAdding({ ...adding, points: pts });
    }
  };
  const onMove = (e) => {
    if (!drag) return;
    const p = toNorm(e);
    setMeasures((ms) => ms.map((m) => (m.id === drag.id ? { ...m, points: m.points.map((q, i) => (i === drag.idx ? p : q)) } : m)));
  };
  const finishAdd = (pts) => {
    const points = pts || adding.points;
    const min = adding.type === "area" ? 3 : 2;
    if (points.length < min) return toast.error(`Placez au moins ${min} points.`);
    const id = `u${Date.now()}`;
    const label = { distance: "Distance", area: "Surface", path: "Parcours" }[adding.type];
    setMeasures((ms) => [...ms, { id, type: adding.type, label: `${label} ${ms.length + 1}`, points, unit: adding.type === "area" ? "m²" : "m", confidence: scale ? "moyenne" : "basse", color: COLORS[ms.length % COLORS.length], ai_value: 0 }]);
    setAdding(null);
  };

  const recalibrate = () => {
    const m = measures.find((x) => x.id === calib.id);
    const real = Number(String(calib.value).replace(",", "."));
    if (!m || !real || real <= 0) return toast.error("Choisissez une mesure et indiquez sa valeur réelle.");
    const { width: w, height: h } = result;
    const px = m.type === "area" ? Math.sqrt(polyArea(m.points, w, h)) : pathLen(m.points, w, h);
    setScale(m.type === "area" ? Math.sqrt(real) / px : real / px);
    setMeasures((ms) => ms.map((x) => ({ ...x, confidence: "haute" })));
    setCalibrated(true);
    toast.success("Échelle recalibrée : toutes les mesures ont été recalculées.");
  };

  const addToQuote = (list) => {
    const lines = list.map((m) => ({
      description: `${m.label} — mesure caméra${scale ? "" : " (estimation IA)"}`, quantity: Math.round(valueOf(m) * 100) / 100, unit: m.unit, unit_price: 0, vat_percent: vatPercent, cost_price: 0, duration_minutes: 0,
    }));
    onAdd(lines);
    toast.success(`${lines.length} ligne${lines.length > 1 ? "s" : ""} ajoutée${lines.length > 1 ? "s" : ""} au devis — complétez le prix unitaire.`);
    onClose();
    reset();
  };

  const [calibrated, setCalibrated] = useState(false);
  const confidenceGlobal = useMemo(() => (calibrated ? "haute" : scale ? (result?.reference ? result.reference.confidence : "moyenne") : "basse"), [scale, result, calibrated]);
  if (!open) return null;

  return (
    <div data-testid="measure-camera" role="dialog" aria-modal="true" className="fixed inset-0 z-[80] flex flex-col bg-white lg:bg-[#0F2645]/60 lg:p-6 lg:backdrop-blur-sm" style={{ animation: "devFadeUp .18s ease-out" }}>
      <div className="mx-auto flex h-full w-full max-w-5xl flex-col overflow-hidden bg-white lg:rounded-3xl lg:shadow-[0_40px_100px_-30px_rgba(15,38,69,0.5)]">
        <div className="flex h-14 shrink-0 items-center gap-3 border-b border-[#E2E7F7] px-4">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[#3A5CC8] text-white"><Camera className="h-4 w-4" /></span>
          <div className="min-w-0 flex-1">
            <p className="font-heading text-base font-bold text-[#0F2645]">Caméra intelligente</p>
            <p className="hidden text-[11px] text-[#5C6A87] sm:block">Mesures sur photo par IA — à confirmer avec une dimension connue pour une précision pro</p>
          </div>
          {result && <button type="button" data-testid="measure-retake" onClick={reset} className={`${btnGhost} min-h-[36px] px-3 text-xs`}><RefreshCw className="h-3.5 w-3.5" /> Nouvelle photo</button>}
          <button type="button" data-testid="measure-close" onClick={() => { onClose(); }} className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-[#5C6A87] hover:bg-[#F3F5FC]" aria-label="Fermer"><X className="h-5 w-5" /></button>
        </div>

        <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="measure-photo-input" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) analyze(f); }} />

        {!result && (
          <div className="flex-1 overflow-y-auto p-5 sm:p-8">
            <p className="text-[11px] font-bold uppercase tracking-wider text-[#93A0BC]">1 · Que voulez-vous mesurer ?</p>
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {GOALS.map((g) => (
                <button key={g.id} type="button" data-testid={`measure-goal-${g.id}`} onClick={() => setGoal(g.id)} className={`flex items-start gap-3 rounded-xl border p-3 text-left transition-colors ${goal === g.id ? "border-[#3A5CC8] bg-[#EEF1FC]" : "border-[#E2E7F7] bg-white hover:border-[#C9D4F4]"}`}>
                  <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${goal === g.id ? "bg-[#3A5CC8] text-white" : "bg-[#F3F5FC] text-[#3A5CC8]"}`}><g.icon className="h-4 w-4" /></span>
                  <span><span className="block text-sm font-semibold text-[#0F2645]">{g.label}</span><span className="block text-xs text-[#5C6A87]">{g.hint}</span></span>
                </button>
              ))}
            </div>
            <p className="mt-6 text-[11px] font-bold uppercase tracking-wider text-[#93A0BC]">2 · Indication (facultatif)</p>
            <input data-testid="measure-hint" value={hint} onChange={(e) => setHint(e.target.value)} placeholder="Ex. : la porte fait 83 cm de large · mesurer le mur du fond" className={`${inputCls} mt-2`} />
            <div className="mt-5 rounded-xl border border-[#C9D4F4] bg-[#F6F8FD] p-3 text-xs leading-relaxed text-[#2F4CA8]">
              <Info className="mr-1.5 inline h-3.5 w-3.5" /> Pour une mesure fiable : photo prise bien de face, et un repère de taille connue dans le cadre (porte, feuille A4, mètre déplié, carte bancaire). L'IA détecte le repère et propose les mesures ; vous ajustez les points et confirmez une dimension réelle.
            </div>
            <button type="button" data-testid="measure-take-photo" disabled={busy} onClick={() => fileRef.current?.click()} className={`${btnPrimary} mt-6 h-12 w-full text-base sm:w-auto`}>
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />} {busy ? "Analyse de la photo…" : "Prendre / choisir la photo"}
            </button>
          </div>
        )}

        {result && (
          <div className="grid min-h-0 flex-1 lg:grid-cols-[minmax(0,1fr)_340px]">
            <div className="relative flex min-h-[260px] items-center justify-center overflow-auto bg-[#0F2645] p-2">
              <div className="relative" style={{ aspectRatio: `${result.width} / ${result.height}`, maxHeight: "100%", maxWidth: "100%", width: result.width >= result.height ? "100%" : "auto", height: result.width < result.height ? "100%" : "auto" }}>
                <img src={result.image} alt="Photo analysée" className="block h-full w-full select-none rounded-lg" draggable={false} />
                <svg ref={svgRef} data-testid="measure-canvas" viewBox="0 0 1000 1000" preserveAspectRatio="none" className={`absolute inset-0 h-full w-full touch-none ${adding ? "cursor-crosshair" : ""}`} onPointerDown={onSvgPointer} onPointerMove={onMove} onPointerUp={() => setDrag(null)} onPointerLeave={() => setDrag(null)}>
                  {result.reference && (
                    <line x1={result.reference.from[0] * 1000} y1={result.reference.from[1] * 1000} x2={result.reference.to[0] * 1000} y2={result.reference.to[1] * 1000} stroke="#FACC15" strokeWidth="4" strokeDasharray="12 8" vectorEffect="non-scaling-stroke" />
                  )}
                  {[...measures, ...(adding ? [{ ...adding, id: "adding", color: "#FFFFFF" }] : [])].map((m) => {
                    const pts = m.points.map((p) => `${p[0] * 1000},${p[1] * 1000}`).join(" ");
                    return (
                      <g key={m.id}>
                        {m.type === "area" ? <polygon points={pts} fill={`${m.color}33`} stroke={m.color} strokeWidth="3" vectorEffect="non-scaling-stroke" /> : <polyline points={pts} fill="none" stroke={m.color} strokeWidth="3" vectorEffect="non-scaling-stroke" />}
                        {m.points.map((p, i) => (
                          <circle key={i} cx={p[0] * 1000} cy={p[1] * 1000} r="14" fill="white" stroke={m.color} strokeWidth="4" vectorEffect="non-scaling-stroke" className="cursor-grab" onPointerDown={(e) => { if (m.id === "adding") return; e.stopPropagation(); e.currentTarget.setPointerCapture?.(e.pointerId); setDrag({ id: m.id, idx: i }); }} />
                        ))}
                      </g>
                    );
                  })}
                </svg>
              </div>
              {adding && (
                <div className="absolute inset-x-0 bottom-3 flex justify-center gap-2 px-3">
                  <span className="rounded-xl bg-white/95 px-3 py-2 text-xs font-semibold text-[#0F2645]">Touchez la photo pour placer les points ({adding.points.length})</span>
                  {adding.type !== "distance" && <button type="button" data-testid="measure-add-finish" onClick={() => finishAdd()} className="rounded-xl bg-[#3A5CC8] px-3 py-2 text-xs font-semibold text-white"><Check className="mr-1 inline h-3.5 w-3.5" />Terminer</button>}
                  <button type="button" onClick={() => setAdding(null)} className="rounded-xl bg-white/95 px-3 py-2 text-xs font-semibold text-[#5C6A87]">Annuler</button>
                </div>
              )}
            </div>

            <div className="flex min-h-0 flex-col overflow-y-auto border-t border-[#E2E7F7] lg:border-l lg:border-t-0">
              <div className="space-y-3 p-4">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-[#93A0BC]">Mesures</p>
                  <span data-testid="measure-confidence" className={`rounded-md border px-2 py-0.5 text-[11px] font-semibold ${CONF[confidenceGlobal] || CONF.basse}`}>Fiabilité {confidenceGlobal}</span>
                </div>
                {result.reference ? (
                  <p data-testid="measure-reference" className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900"><span className="font-semibold">Repère détecté :</span> {result.reference.label} ≈ {result.reference.real_cm} cm (trait jaune)</p>
                ) : (
                  <p className="rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-800">Aucun repère fiable détecté : valeurs indicatives. Confirmez une dimension connue ci-dessous.</p>
                )}
                {measures.length === 0 && <p className="text-sm text-[#5C6A87]">Ajoutez une mesure avec les boutons ci-dessous.</p>}
                <ul className="space-y-2">
                  {measures.map((m) => (
                    <li key={m.id} data-testid={`measure-item-${m.id}`} className="flex items-center gap-2 rounded-xl border border-[#E2E7F7] bg-white p-2.5">
                      <span className="h-8 w-1.5 shrink-0 rounded-full" style={{ background: m.color }} />
                      <div className="min-w-0 flex-1">
                        <input data-testid={`measure-label-${m.id}`} value={m.label} onChange={(e) => setMeasures((ms) => ms.map((x) => (x.id === m.id ? { ...x, label: e.target.value } : x)))} title={m.label} className="w-full truncate bg-transparent text-sm font-semibold text-[#0F2645] focus:outline-none" />
                        <p className="text-[11px] text-[#5C6A87]">{{ distance: "Distance", area: "Surface", path: "Parcours" }[m.type]} · {m.points.length} pts · <span className={`rounded border px-1 ${CONF[m.confidence] || CONF.basse}`}>{m.confidence}</span></p>
                      </div>
                      <p data-testid={`measure-value-${m.id}`} className="shrink-0 font-mono text-sm font-bold tabular-nums text-[#0F2645]">{fmt(valueOf(m), m.unit)}</p>
                      <button type="button" onClick={() => setMeasures((ms) => ms.filter((x) => x.id !== m.id))} className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[#C9D4F4] hover:bg-rose-50 hover:text-rose-600" aria-label="Supprimer"><Trash2 className="h-4 w-4" /></button>
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-1.5">
                  {[["distance", "Distance", Ruler], ["area", "Surface", Square], ["path", "Parcours", Route]].map(([t, l, I]) => (
                    <button key={t} type="button" data-testid={`measure-add-${t}`} disabled={!!adding} onClick={() => setAdding({ type: t, points: [] })} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-[#E2E7F7] bg-white px-2.5 text-xs font-semibold text-[#0F2645] hover:border-[#C9D4F4] disabled:opacity-40"><Plus className="h-3.5 w-3.5 text-[#3A5CC8]" /><I className="h-3.5 w-3.5" /> {l}</button>
                  ))}
                </div>

                <div className="rounded-xl border border-[#C9D4F4] bg-[#F6F8FD] p-3">
                  <p className="text-xs font-bold text-[#2F4CA8]">Je connais une dimension réelle</p>
                  <div className="mt-2 flex gap-2">
                    <select data-testid="measure-calib-id" value={calib.id} onChange={(e) => setCalib({ ...calib, id: e.target.value })} className={`${inputCls} !w-auto min-w-0 flex-1 py-2 text-sm`}>
                      <option value="">Choisir la mesure…</option>
                      {measures.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                    </select>
                    <input data-testid="measure-calib-value" value={calib.value} onChange={(e) => setCalib({ ...calib, value: e.target.value })} placeholder="ex. 2,40" inputMode="decimal" className={`${inputCls} !w-24 shrink-0 py-2 text-sm`} />
                    <button type="button" data-testid="measure-calib-apply" onClick={recalibrate} className="inline-flex h-[42px] shrink-0 items-center rounded-xl bg-[#3A5CC8] px-3 text-xs font-semibold text-white"><Check className="h-4 w-4" /></button>
                  </div>
                </div>

                {result.notes && <p data-testid="measure-notes" className="text-xs leading-relaxed text-[#5C6A87]">{result.notes}</p>}
                {result.suggestions?.length > 0 && (
                  <div>
                    <p className="mb-1.5 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[#93A0BC]"><Lightbulb className="h-3 w-3 text-amber-500" /> Et ensuite ?</p>
                    <ul className="space-y-1">{result.suggestions.map((s) => <li key={s} className="rounded-lg bg-[#F6F8FD] px-2.5 py-1.5 text-xs text-[#0F2645]">{s}</li>)}</ul>
                  </div>
                )}
              </div>
              <div className="sticky bottom-0 mt-auto border-t border-[#E2E7F7] bg-white p-3">
                <button type="button" data-testid="measure-add-to-quote" disabled={!measures.length} onClick={() => addToQuote(measures)} className={`${btnPrimary} h-12 w-full`}>
                  <Plus className="h-4 w-4" /> Ajouter {measures.length || ""} ligne{measures.length > 1 ? "s" : ""} au devis
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
